'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { useDashboard } from '@/components/dashboard/DashboardContext';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ThemeSwitcher } from '@/components/ui/theme-provider';
import {
  Menu,
  Settings,
} from 'lucide-react';
import Link from 'next/link';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useRouter, usePathname } from 'next/navigation';

// 경로 세그먼트 표시 이름 매핑
const pathSegmentNames: Record<string, string> = {
  "dashboard": "대시보드",
  "settings": "설정",
  "user": "사용자",
  "welcome-message": "환영 메시지",
  "permissions": "권한 관리",
  "tts": "TTS 설정",
  "features": "기능 설정",
  "channel-default": "채널 기본값",
  "vchannel": "임시 음성 채널",
  "translate": "자동 번역",
  "ai-toolkit": "AI 툴킷",
  "ai-chat": "AI 채팅",
  "cleanup": "자동 삭제 타이머",
};

// 브레드크럼에서 제외할 세그먼트 (표시에서만 제외됨, 실제 URL에는 포함)
const excludedSegments = ['settings', 'channels'];

// BreadcrumbItem 인터페이스 정의
interface BreadcrumbItem {
  label: string;
  href: string;
}

const Header = () => {
  const { user, logout } = useAuth();
  const { currentGuildId, currentChannelId, currentChannel, guilds, toggleSidebar } = useDashboard();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [showMenuTip, setShowMenuTip] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  // 선택된 서버 정보 찾기
  const selectedGuild = guilds.find(guild => guild.id === currentGuildId);

  // 현재 경로가 사용자 설정 페이지인지 확인
  const isUserSettingsPage = pathname?.includes('/dashboard/user/settings');

  // 사용자 ID로부터 기본 Discord 아바타 인덱스 계산
  const getDefaultAvatarIndex = (userId: string) => {
    return (BigInt(userId) >> BigInt(22)) % BigInt(6);
  };

  // 사용자 아바타 URL 생성
  const getUserEffectiveAvatarUrl = (user: any) => {
    if (user?.avatar) {
      return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`;
    } else {
      const avatarIndex = getDefaultAvatarIndex(user.id);
      return `https://cdn.discordapp.com/embed/avatars/${avatarIndex}.png`;
    }
  };

  // 경로 세그먼트를 기반으로 브레드크럼 아이템 생성
  const breadcrumbs = useMemo(() => {
    const items: BreadcrumbItem[] = [
      { label: "카미봇", href: "/dashboard" }
    ];

    if (!pathname) return items;

    const segments = pathname.split('/').filter(Boolean);

    // 기본 대시보드 경로
    if (segments.length === 1 && segments[0] === 'dashboard') {
      return items;
    }

    // 사용자 설정 페이지
    if (segments.includes('user') && segments.includes('settings')) {
      return [
        ...items,
        { label: "내 설정", href: "/dashboard/user/settings" }
      ];
    }

    // 서버 관련 경로
    if (segments.length > 1 && segments[0] === 'dashboard' && segments[1] === currentGuildId) {
      // 서버 이름 추가
      items.push({
        label: selectedGuild?.name || "서버",
        href: `/dashboard/${currentGuildId}`
      });

      // 추가 세부 경로 처리
      if (segments.length > 2) {
        let currentPath = `/dashboard/${currentGuildId}`;
        let currentSegments = []; // 현재까지의 세그먼트를 저장

        for (let i = 2; i < segments.length; i++) {
          const segment = segments[i];
          currentPath += `/${segment}`;
          currentSegments.push(segment);

          // 현재 세그먼트가 제외 대상이면 브레드크럼에 표시하지 않음
          if (excludedSegments.includes(segment)) {
            continue;
          }

          // 채널 ID인 경우 채널 이름으로 대체해서 표시
          if (segment === currentChannelId && currentChannel) {
            items.push({
              label: `# ${currentChannel.name}`,
              href: currentPath // 실제 URL에는 channels 세그먼트를 포함
            });
          }
          // 다음 세그먼트가 있고 그것이 제외 목록에 있는 세그먼트의 자식이면 표시
          else if (i + 1 < segments.length && excludedSegments.includes(segment)) {
            // 이 경우 다음 세그먼트를 표시할 것이므로 여기서는 건너뜀
            continue;
          }
          // 일반 세그먼트는 그대로 표시
          else {
            items.push({
              label: pathSegmentNames[segment] || segment,
              href: currentPath
            });
          }
        }
      }
    }

    return items;
  }, [pathname, currentGuildId, currentChannelId, currentChannel, selectedGuild]);

  // 처음 방문하는 사용자에게 툴팁 표시 여부 확인
  useEffect(() => {
    if (!isDesktop) {
      const hasVisitedBefore = localStorage.getItem('hasVisitedDashboard');
      // 이전에 방문한 적이 없으면 툴팁을 표시
      setShowMenuTip(!hasVisitedBefore);
    }
  }, [isDesktop]);

  return (
    <header className="sticky top-0 z-30 w-full border-b bg-background">
      <div className="flex h-14 items-center px-4 justify-between">
        <div className="flex items-center">
          {/* 모바일에서만 햄버거 메뉴 표시 */}
          {!isDesktop && (
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="mr-2"
                onClick={() => {
                  toggleSidebar();
                  setShowMenuTip(false);
                  localStorage.setItem('hasVisitedDashboard', 'true');
                }}
                aria-label="메뉴"
              >
                <Menu className="h-5 w-5" />
              </Button>

              {/* 신규 사용자를 위한 툴팁 - 사용자가 클릭할 때까지 계속 표시 */}
              {showMenuTip && (
                <div className="absolute left-0 top-full mt-1 bg-primary text-primary-foreground px-3 py-2 rounded-md text-sm whitespace-nowrap animate-pulse">
                  여기를 눌러 서버, 채널을 고르세요!
                  <div className="absolute left-3 -top-1 w-2 h-2 bg-primary rotate-45"></div>
                </div>
              )}
            </div>
          )}

          {/* 경로 네비게이션 */}
          <nav aria-label="경로 네비게이션">
            <ol className="flex items-center flex-wrap">
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={item.href}>
                  {/* 경로 항목 */}
                  <li>
                    <Link
                      href={item.href}
                      className={`text-lg font-semibold ${index === breadcrumbs.length - 1 ? 'text-foreground' : 'hover:text-primary transition-colors'}`}
                      aria-current={index === breadcrumbs.length - 1 ? 'page' : undefined}
                    >
                      {item.label}
                    </Link>
                  </li>
                  {/* 구분자 (마지막 항목 제외) */}
                  {index < breadcrumbs.length - 1 && (
                    <li className="mx-2 text-muted-foreground">»</li>
                  )}
                </React.Fragment>
              ))}
            </ol>
          </nav>
        </div>

        <div className="flex items-center gap-2">
          {/* 테마 전환 스위치 */}
          <div className="mr-1">
            <ThemeSwitcher />
          </div>

          {/* 설정 버튼 */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size={isDesktop ? "default" : "icon"}
                  asChild
                >
                  <Link href={'/dashboard/user/settings'}>
                    <div className="flex items-center justify-center">
                      <Settings className={`h-4 w-4 ${isDesktop ? 'mr-2' : ''}`} />
                      {isDesktop && '내 설정'}
                    </div>
                  </Link>
                </Button>
              </TooltipTrigger>
              {!isDesktop && (
                <TooltipContent>
                  내 설정
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          {/* 유저 아바타 */}
          <Avatar
            className="h-8 w-8 cursor-pointer"
            title="사용자 설정"
            onClick={() => {
              // 사용자 설정 페이지로 이동
              router.push('/dashboard/user/settings');
            }}
          >
            <AvatarImage
              src={user ? getUserEffectiveAvatarUrl(user) : undefined}
              alt={user?.global_name || user?.username}
            />
            <AvatarFallback>
              {user?.global_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
        </div>
      </div>
    </header>
  );
};

export default Header;
